import React, { useEffect, useState, useCallback, useRef } from 'react';

// helpers
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { useStyles } from '../ExaminationTable/ExaminationTable.style';
import * as actions from '../../../../../store/index';
import { formatSimpleDateHour } from '../../../../../helpers/formatDate';
import { formatPatientName } from '../../../../../helpers/misc';

// material-ui
import { useTheme } from '@material-ui/core/styles';
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
  Checkbox,
  Typography,
  IconButton,
  Collapse,
  CircularProgress,
  Tooltip,
  Divider,
} from '@material-ui/core';

// icons
import CollectionsIcon from '@material-ui/icons/Collections';
import NotIcon from '@material-ui/icons/NotInterested';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import animation from '../../../../../resources/processing_animation.gif';
import HowToRegIcon from '@material-ui/icons/HowToReg';
import ExaminationTableActions from '../ExaminationTable/Helpers/ExaminationTableActions';
import { examinationTooltips } from '../ExaminationTable/Helpers/ExaminationTableCommon';
import ExaminationTableRisk from '../ExaminationTable/Helpers/ExaminationTableRisk';
import ControlPointIcon from '@material-ui/icons/ControlPoint';
import RepeatIcon from '@material-ui/icons/Repeat';
import TouchAppIcon from '@material-ui/icons/TouchApp';
import GetAppSharpIcon from '@material-ui/icons/GetAppSharp';
import { movePriorExaminations } from '../../../../../store/index';
import { fetchPost } from '../../../../../helpers/fetching';

const ExaminationTableRows = (props) => {
  const theme = useTheme();
  const classes = useStyles();
  const currentRowRef = useRef(null);
  const endListRef = useRef(null);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const currentRole = localStorage.getItem('role');

  const { localChangesDensity, localChangesQuality, localChangesMicrocalc, localChangesOpacities } =
    useSelector((state) => state.labeling);
  const {
    currentExam,
    exam_list,
    fetchingExamList,
    currentlyProcessed,
    pendingReprocessing,
    totalExaminations,
    pagination,
    priors_list,
    bulkAction,
    bulkExaminationsSelected,
  } = useSelector((state) => state.examinations);
  const { diagnostics_microcalc_module, diagnostics_opacities_module, density_module, risk_module } =
    useSelector((state) => state.global_settings.premium_features);

  const [showIcons, setShowIcons] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [hoveredPriorIndex, setHoveredPriorIndex] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const [isHidden, setIsHidden] = useState(true);

  const managementAccess =
    currentRole === 'admin' || currentRole === 'supervisor' || currentRole === 'radiologist';

  const getColumnCellStyle = (key) => {
    const width = props.columnWidths?.[key];
    if (!width) return {};
    return {
      width,
      minWidth: width,
      maxWidth: width,
      boxSizing: 'border-box',
      ...(key === 'expand' || key === 'bulk' ? { padding: 0, overflow: 'hidden' } : {}),
    };
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsHidden(false);
    }, 6000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const examsWithCurrentId = props.rows.filter((row) => row.some((exam) => exam.id === currentExam));
    const hasMultipleExams = examsWithCurrentId?.[0]?.length > 1;
    if (currentExam && hasMultipleExams) {
      examsWithCurrentId.forEach((row) => {
        const patientId = row[0]?.patient_id || row[1]?.patient_id;
        if (patientId) {
          setExpandedRows((prevState) => ({
            ...prevState,
            [patientId]: true,
          }));
        }
      });
    }
  }, [props.rows, currentExam]);

  const handleRowClick = (rowId) => {
    setExpandedRows((prevState) => ({
      ...prevState,
      [rowId]: !prevState[rowId],
    }));
  };

  const handleHover = useCallback((id) => {
    setHoveredIndex(id);
    setShowIcons(true);
  }, []);

  const handleLeave = useCallback(() => {
    setHoveredIndex(null);
    setShowIcons(false);
  }, []);

  const nestedPatientSelected = (nestedRows) => {
    const examExists = nestedRows.some((row) => row.id === currentExam);
    return examExists;
  };

  const fetchMorePatients = () => {
    dispatch(actions.setPagination([pagination[0], pagination[1] + 30]));
  };

  const onRowClick = (currentId, isPrior) => {
    dispatch(actions.setScrollPosition(props.currentTableRow));

    if (localChangesDensity || localChangesQuality || localChangesMicrocalc || localChangesOpacities) {
      dispatch(actions.setActiveDialog('unsavedChanges'));
    } else {
      if (isPrior) {
        dispatch(
          actions.setActionToConfirm({
            action: () => {
              dispatch(movePriorExaminations([currentId], t));
            },
            text: t('Confirm.move_prior_confirm'),
            dialogTitle: t('Confirm.move_prior_title'),
            buttonOk: t('Confirm.move_prior_btn'),
          }),
        );
      } else {
        dispatch(actions.showExamination(currentId));
        if (props.expandedPatientList) {
          setTimeout(() => {
            dispatch(actions.setExaminationListDrawer(false));
          }, 1000);
        }
      }
    }
  };

  const toggleBulkExamSelection = (examId) => {
    if (!examId) return;

    const isSelected = bulkExaminationsSelected.includes(examId);
    const updatedSelection = isSelected
      ? bulkExaminationsSelected.filter((id) => id !== examId)
      : [...bulkExaminationsSelected, examId];

    dispatch(actions.setBulkExaminationsSelected(updatedSelection));
  };

  const toggleBulkRowSelection = (row) => {
    const rowExamIds = row.filter((item) => !item.is_prior).map((item) => item.id);
    if (rowExamIds.length === 0) return;

    const isEverySelected = rowExamIds.every((examId) => bulkExaminationsSelected.includes(examId));
    const updatedSelection = isEverySelected
      ? bulkExaminationsSelected.filter((id) => !rowExamIds.includes(id))
      : [...new Set([...bulkExaminationsSelected, ...rowExamIds])];

    dispatch(actions.setBulkExaminationsSelected(updatedSelection));
  };

  useEffect(() => {
    if (!bulkAction && bulkExaminationsSelected.length > 0) {
      dispatch(actions.setBulkExaminationsSelected([]));
    }
  }, [bulkAction, bulkExaminationsSelected.length, dispatch]);

  useEffect(() => {
    if (!bulkAction) return;
    const allExamIds = props.rows
      .flatMap((row) => row.filter((item) => !item.is_prior).map((item) => item.id))
      .filter(Boolean);
    const isSelectAll =
      allExamIds.length > 0 && allExamIds.every((examId) => bulkExaminationsSelected.includes(examId));

    if (!isSelectAll) return;

    const expandedState = {};
    props.rows.forEach((row) => {
      const patientId = row?.[0]?.patient_id;
      if (patientId && row.length > 1) expandedState[patientId] = true;
    });
    setExpandedRows((prev) => ({ ...prev, ...expandedState }));
  }, [bulkAction, bulkExaminationsSelected, props.rows]);

  // Get age from birthdate
  const getAge = (birthDate) => {
    if (!birthDate) return '';
    const bd = new Date(birthDate);
    if (isNaN(bd.getTime())) return '';
    const today = new Date();
    let age = today.getFullYear() - bd.getFullYear();
    const m = today.getMonth() - bd.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < bd.getDate())) {
      age--;
    }
    return age;
  };

  const nestedTable = (item, i) => {
    //Nested table
    return (
      <TableRow
        onMouseOver={() => handleHover(item.id)}
        onMouseLeave={handleLeave}
        className={item.id === currentExam ? classes.tr_nested_hover_selected : classes.tr_nested_hover}
        hover
        key={`${item.id}${item.patient_id}${i}`}
        selected={item.id === currentExam}
        ref={item.id === currentExam ? currentRowRef : null}>
        <TableCell className={classes.tr_nested} style={{ ...getColumnCellStyle('expand'), padding: '1px' }}>
          {bulkAction && !item.is_prior && (
            <Checkbox
              className={classes.bulkCheckbox}
              size='small'
              color='primary'
              checked={bulkExaminationsSelected.includes(item.id)}
              onClick={(event) => event.stopPropagation()}
              onChange={() => toggleBulkExamSelection(item.id)}
            />
          )}
        </TableCell>

        {/* Name */}
        <TableCell
          className={classes.tr_nested}
          key='patient_name'
          align='left'
          onClick={() => onRowClick(item.id, item.is_prior)}
          style={{
            ...getColumnCellStyle('name'),
            fontWeight: item.id === currentExam && 'bold',
            color: item.is_prior && '#999999',
          }}>
          {item.is_prior ? (
            <Tooltip
              arrow
              placement='right-start'
              title={
                <Typography className={classes.tooltip_texts}>
                  {`${t('Table.prior_exam')}.
                  ${t('Table.move_unique_prior')}`}
                </Typography>
              }>
              <span>{item.name ? formatPatientName(item.name) : t('Table.pending_pull')}</span>
            </Tooltip>
          ) : (
            formatPatientName(item.name)
          )}
        </TableCell>

        {/* Patient DOB */}
        {props.expandedPatientList && (
          <TableCell
            className={classes.tr_nested}
            onClick={() => onRowClick(item.id, item.is_prior)}
            style={getColumnCellStyle('dob')}
          />
        )}

        {/* Patient ID */}
        {props.expandedPatientList && (
          <TableCell
            className={classes.tr_nested}
            onClick={() => onRowClick(item.id, item.is_prior)}
            style={getColumnCellStyle('patientId')}
          />
        )}

        {/* Info */}
        <TableCell
          className={classes.tr_nested}
          align='center'
          key='icons'
          onClick={() => onRowClick(item.id, item.is_prior)}
          style={getColumnCellStyle('icons')}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
            {item?.density_confirmed ||
            item?.quality_confirmed ||
            item?.microcalc_confirmed ||
            item?.opacities_confirmed
              ? examinationTooltips(
                  <>
                    {density_module && (
                      <Typography className={classes.tooltip_texts_margin}>
                        {t('ResultView.density')}:
                        {item?.density_confirmed
                          ? t('Table.module_confirmed')
                          : t('Table.module_not_confirmed')}
                      </Typography>
                    )}
                    <Typography className={classes.tooltip_texts_margin}>
                      {t('ResultView.quality')}:
                      {item?.quality_confirmed
                        ? t('Table.module_confirmed')
                        : t('Table.module_not_confirmed')}
                    </Typography>
                    {diagnostics_opacities_module && localStorage.getItem('diagnostics') === 'true' && (
                      <Typography className={classes.tooltip_texts_margin}>
                        {t('ResultView.diagnose_opacities')}:
                        {item?.opacities_confirmed
                          ? t('Table.module_confirmed')
                          : t('Table.module_not_confirmed')}
                      </Typography>
                    )}
                    {diagnostics_microcalc_module && localStorage.getItem('diagnostics') === 'true' && (
                      <Typography className={classes.tooltip_texts_margin}>
                        {t('ResultView.diagnose_microcalc')}:
                        {item?.microcalc_confirmed
                          ? t('Table.module_confirmed')
                          : t('Table.module_not_confirmed')}
                      </Typography>
                    )}
                  </>,
                  <HowToRegIcon
                    fontSize='inherit'
                    style={{
                      opacity: fetchingExamList && '0.5',
                    }}
                  />,
                  'top-start',
                )
              : ''}
            {showIcons && item.id === hoveredIndex && (
              <>
                {item?.max_captures_per_projection > 1 &&
                  examinationTooltips(
                    <Typography className={classes.tooltip_texts}>{t('Table.captures')}</Typography>,
                    <CollectionsIcon fontSize='inherit' />,
                  )}
                {!!item?.not_evaluated &&
                  examinationTooltips(
                    <Typography className={classes.tooltip_texts}>{t('Table.not_eval')}</Typography>,
                    <NotIcon fontSize='inherit' />,
                  )}
              </>
            )}
          </div>

          {/* Priors icon */}
        </TableCell>

        {/* Examination Date */}
        <TableCell
          align='center'
          key='date'
          onClick={() => onRowClick(item.id, item.is_prior)}
          style={{
            ...getColumnCellStyle('date'),
            color: item.is_prior && '#999999',
          }}
          className={classes.tr_nested}>
          {examinationTooltips(
            formatSimpleDateHour(item.date).hour,
            <Typography
              className={classes.table_date_font}
              style={{
                fontWeight: item.id === currentExam && 'bold',
              }}>
              {formatSimpleDateHour(item.date).date}
            </Typography>,
            'top-start',
          )}
        </TableCell>

        {/* Risk (shown or hidden from .env)*/}
        <TableCell
          className={classes.tr_nested}
          key='risk'
          align='right'
          onClick={() => onRowClick(item.id, item.is_prior)}
          style={{ width: '30px !important' }}>
          {risk_module && item.name && (
            <ExaminationTableRisk exam_id={item.id} form_filled={item.form_filled} exam_list={exam_list} />
          )}
        </TableCell>

        {/* Actions */}
        <ExaminationTableActions
          study_id={item.id}
          patient_id={item.patient_id}
          isNestedExam={true}
          nestedRowTitle={false}
          singleExam={false}
          prior={item.is_prior}
          isRemoved={!!item.is_removed}
          priorList={null}
          columnWidth={props.columnWidths?.actions}
        />
      </TableRow>
    );
  };

  const nestedRowIcons = (row, study_id) => {
    // Aggregate conditions across all nested examinations
    const hasAnyConfirmed = row.some(
      (item) =>
        item.density_confirmed ||
        item.quality_confirmed ||
        item.opacities_confirmed ||
        item.microcalc_confirmed,
    );
    const hasAnyMaxCaptures = row.some((item) => item.max_captures_per_projection > 1);
    const hasAnyNotEvaluated = row.some((item) => !!item.not_evaluated);

    // Check if any nested examination has specific confirmed flags (for tooltip)
    const hasDensityConfirmed = row.some((item) => !!item.density_confirmed);
    const hasQualityConfirmed = row.some((item) => !!item.quality_confirmed);
    const hasOpacitiesConfirmed = row.some((item) => !!item.opacities_confirmed);
    const hasMicrocalcConfirmed = row.some((item) => !!item.microcalc_confirmed);

    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '30px',
        }}>
        {hasAnyConfirmed &&
          examinationTooltips(
            <>
              {density_module && (
                <Typography className={classes.tooltip_texts_margin}>
                  {t('ResultView.density')}:
                  {hasDensityConfirmed ? t('Table.module_confirmed') : t('Table.module_not_confirmed')}
                </Typography>
              )}
              <Typography className={classes.tooltip_texts_margin}>
                {t('ResultView.quality')}:
                {hasQualityConfirmed ? t('Table.module_confirmed') : t('Table.module_not_confirmed')}
              </Typography>
              {diagnostics_opacities_module && localStorage.getItem('diagnostics') === 'true' && (
                <Typography className={classes.tooltip_texts_margin}>
                  {t('ResultView.diagnose_opacities')}:
                  {hasOpacitiesConfirmed ? t('Table.module_confirmed') : t('Table.module_not_confirmed')}
                </Typography>
              )}
              {diagnostics_microcalc_module && localStorage.getItem('diagnostics') === 'true' && (
                <Typography className={classes.tooltip_texts_margin}>
                  {t('ResultView.diagnose_microcalc')}:
                  {hasMicrocalcConfirmed ? t('Table.module_confirmed') : t('Table.module_not_confirmed')}
                </Typography>
              )}
            </>,
            <HowToRegIcon
              fontSize='inherit'
              style={{
                opacity: fetchingExamList && '0.5',
              }}
            />,
          )}
        {showIcons && study_id === hoveredIndex && (
          <>
            {hasAnyMaxCaptures &&
              examinationTooltips(
                <Typography className={classes.tooltip_texts}>{t('Table.captures')}</Typography>,
                <CollectionsIcon fontSize='inherit' style={{ opacity: fetchingExamList && '0.5' }} />,
              )}
            {hasAnyNotEvaluated &&
              examinationTooltips(
                <Typography className={classes.tooltip_texts}>{t('Table.not_eval')}</Typography>,
                <NotIcon fontSize='inherit' style={{ opacity: fetchingExamList && '0.5' }} />,
              )}
          </>
        )}
      </div>
    );
  };

  return (
    <TableBody style={{ cursor: fetchingExamList ? 'progress' : 'pointer' }}>
      {fetchingExamList && (
        <TableRow
          style={{
            position: 'absolute',
            top: '50%',
            opacity: '0.6',
            width: '99%',
            display: 'flex',
            justifyContent: 'center',
          }}>
          <TableCell style={{ border: 'none' }}>
            <img
              style={{
                display: 'block',
                margin: 'auto',
                width: 'auto',
                height: '100px',
                borderRadius: '50%',
              }}
              src={animation}
              alt='logoanimation'
            />
          </TableCell>
        </TableRow>
      )}
      {props.rows.map((row, idx) => {
        const {
          id: study_id,
          name,
          date,
          density_confirmed,
          quality_confirmed,
          opacities_confirmed,
          microcalc_confirmed,
          not_evaluated,
          form_filled,
          patient_bd,
          max_captures_per_projection,
          patient_id,
          priors_found,
          is_removed,
        } = row[0];

        const isRowExpanded = expandedRows[patient_id] || false;
        const rowExamIds = row.filter((item) => !item.is_prior).map((item) => item.id);
        const isRowFullySelected =
          rowExamIds.length > 0 && rowExamIds.every((examId) => bulkExaminationsSelected.includes(examId));
        const isRowPartiallySelected =
          !isRowFullySelected && rowExamIds.some((examId) => bulkExaminationsSelected.includes(examId));

        return (
          <React.Fragment key={idx + name}>
            <TableRow
              key={idx + name}
              hover
              onMouseOver={() => handleHover(study_id)}
              onMouseLeave={handleLeave}
              className={classes.tr_hover}
              selected={study_id === currentExam}
              style={{
                background: isRowExpanded && theme.palette.background.highlightLight,
              }}>
              {bulkAction && (
                <TableCell
                  className={classes.tr_padding}
                  style={getColumnCellStyle('bulk')}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                      width: '100%',
                    }}>
                    <Checkbox
                      className={classes.bulkCheckbox}
                      size='small'
                      color='primary'
                      checked={isRowFullySelected}
                      indeterminate={isRowPartiallySelected}
                      onClick={(event) => {
                        event.stopPropagation();
                        row.length > 1 && !isRowExpanded && handleRowClick(patient_id);
                      }}
                      onChange={() => toggleBulkRowSelection(row)}
                    />
                  </div>
                </TableCell>
              )}
              {/* Expand icon */}
              <TableCell padding='none' style={getColumnCellStyle('expand')}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <IconButton
                    aria-label='expand row'
                    size='small'
                    onClick={() => row.length > 1 && handleRowClick(patient_id)}
                    style={{
                      width: '22px',
                      height: '22px',
                      minWidth: '22px',
                      maxWidth: '22px',
                      padding: 0,
                    }}>
                    {row.length > 1 &&
                      (isRowExpanded ? (
                        <ExpandLessIcon fontSize='small' />
                      ) : (
                        <ExpandMoreIcon fontSize='small' />
                      ))}
                  </IconButton>
                </div>
              </TableCell>

              {/* Name */}
              <TableCell
                className={classes.table_name_cell_container}
                component='th'
                align='left'
                onClick={() => onRowClick(study_id)}
                style={{ ...getColumnCellStyle('name'), color: fetchingExamList && '#a1a1a1' }}>
                {examinationTooltips(
                  <>
                    <Typography className={classes.tooltip_texts}>
                      {`${t('ResultView.dob')}: ${formatSimpleDateHour(patient_bd).date}`}
                    </Typography>
                    {!props.expandedPatientList && (
                      <>
                        <Divider style={{ margin: '3%' }} />
                        <Typography className={classes.tooltip_texts}>
                          {`${t('ResultView.pat_id')}: ${patient_id}`}
                        </Typography>
                      </>
                    )}
                  </>,
                  <Typography
                    className={
                      (!isRowExpanded && study_id === currentExam) ||
                      (row.length > 1 && nestedPatientSelected(row) && !isRowExpanded)
                        ? classes.row_selected
                        : classes.table_font
                    }>
                    {formatPatientName(name)}
                  </Typography>,
                  'top',
                )}
              </TableCell>

              {/* Patient DOB when list expanded */}
              {props.expandedPatientList && (
                <TableCell
                  className={classes.table_cell_container}
                  component='th'
                  align='left'
                  onClick={() => onRowClick(study_id)}
                  style={{ ...getColumnCellStyle('dob'), color: fetchingExamList && '#a1a1a1' }}>
                  <Tooltip
                    title={
                      <Typography className={classes.tooltip_texts}>
                        {t('patient_risk.age')}: {getAge(patient_bd)}
                      </Typography>
                    }
                    placement='top'>
                    <Typography
                      className={
                        study_id === currentExam || nestedPatientSelected(row)
                          ? classes.row_selected
                          : classes.table_date_font
                      }>
                      {formatSimpleDateHour(patient_bd).date}
                    </Typography>
                  </Tooltip>
                </TableCell>
              )}

              {/* Patient ID when list expanded */}
              {props.expandedPatientList && (
                <TableCell
                  className={classes.table_name_cell_container}
                  component='th'
                  align='left'
                  onClick={() => onRowClick(study_id)}
                  style={{ ...getColumnCellStyle('patientId'), color: fetchingExamList && '#a1a1a1' }}>
                  {examinationTooltips(
                    <>
                      <Typography className={classes.tooltip_texts}>
                        {`${t('ResultView.pat_id')}: ${patient_id}`}
                      </Typography>
                    </>,
                    <Typography
                      className={
                        (!isRowExpanded && study_id === currentExam) ||
                        (row.length > 1 && nestedPatientSelected(row) && !isRowExpanded)
                          ? classes.row_selected
                          : classes.table_font
                      }>
                      {patient_id}
                    </Typography>,
                    'top',
                  )}
                </TableCell>
              )}

              {/* Info */}
              {row.length > 1 ? (
                isRowExpanded ? (
                  <TableCell
                    className={classes.tr_icons}
                    align='right'
                    onClick={() => onRowClick(study_id)}
                    style={getColumnCellStyle('icons')}
                  />
                ) : (
                  <TableCell
                    className={classes.tr_icons}
                    align='right'
                    onClick={() => onRowClick(study_id)}
                    style={getColumnCellStyle('icons')}>
                    {nestedRowIcons(row, study_id)}
                  </TableCell>
                )
              ) : (
                <TableCell
                  className={classes.tr_icons}
                  align='right'
                  onClick={() => onRowClick(study_id)}
                  style={getColumnCellStyle('icons')}>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      width: '100%',
                    }}>
                    {[density_confirmed, quality_confirmed, opacities_confirmed, microcalc_confirmed].some(
                      (a) => !!a,
                    ) &&
                      examinationTooltips(
                        <>
                          {density_module && (
                            <Typography className={classes.tooltip_texts_margin}>
                              {t('ResultView.density')}:
                              {density_confirmed
                                ? t('Table.module_confirmed')
                                : t('Table.module_not_confirmed')}
                            </Typography>
                          )}
                          <Typography className={classes.tooltip_texts_margin}>
                            {t('ResultView.quality')}:
                            {quality_confirmed
                              ? t('Table.module_confirmed')
                              : t('Table.module_not_confirmed')}
                          </Typography>
                          {diagnostics_opacities_module && localStorage.getItem('diagnostics') === 'true' && (
                            <Typography className={classes.tooltip_texts_margin}>
                              {t('ResultView.diagnose_opacities')}:
                              {opacities_confirmed
                                ? t('Table.module_confirmed')
                                : t('Table.module_not_confirmed')}
                            </Typography>
                          )}
                          {diagnostics_microcalc_module && localStorage.getItem('diagnostics') === 'true' && (
                            <Typography className={classes.tooltip_texts_margin}>
                              {t('ResultView.diagnose_microcalc')}:
                              {microcalc_confirmed
                                ? t('Table.module_confirmed')
                                : t('Table.module_not_confirmed')}
                            </Typography>
                          )}
                        </>,
                        <HowToRegIcon
                          fontSize='inherit'
                          style={{
                            opacity: fetchingExamList && '0.5',
                            // fill: 'rgb(76, 175, 80)',
                          }}
                        />,
                      )}
                    {showIcons && study_id === hoveredIndex && (
                      <>
                        {max_captures_per_projection > 1 &&
                          examinationTooltips(
                            <Typography className={classes.tooltip_texts}>{t('Table.captures')}</Typography>,
                            <CollectionsIcon
                              fontSize='inherit'
                              style={{ opacity: fetchingExamList && '0.5' }}
                            />,
                          )}
                        {!!not_evaluated &&
                          examinationTooltips(
                            <Typography className={classes.tooltip_texts}>{t('Table.not_eval')}</Typography>,
                            <NotIcon fontSize='inherit' style={{ opacity: fetchingExamList && '0.5' }} />,
                          )}
                      </>
                    )}
                  </div>
                </TableCell>
              )}

              {/* Examination Date */}
              {isRowExpanded && row.length > 1 ? (
                <TableCell
                  className={classes.table_cell_container}
                  align='center'
                  style={getColumnCellStyle('date')}
                />
              ) : (
                <TableCell
                  className={classes.table_cell_container}
                  align='left'
                  onClick={() => onRowClick(study_id)}
                  style={{ ...getColumnCellStyle('date'), color: fetchingExamList && '#a1a1a1' }}>
                  {examinationTooltips(
                    <Typography className={classes.tooltip_texts}>
                      {formatSimpleDateHour(date).hour}
                    </Typography>,
                    <Typography
                      className={
                        study_id === currentExam || nestedPatientSelected(row)
                          ? classes.row_selected
                          : classes.table_date_font
                      }>
                      {formatSimpleDateHour(date).date}
                    </Typography>,
                    'top-start',
                  )}
                </TableCell>
              )}

              {/* Risk */}
              {isRowExpanded && row.length > 1 ? (
                <TableCell className={classes.tr_padding} align='center' style={getColumnCellStyle('risk')} />
              ) : (
                <TableCell
                  className={classes.tr_padding}
                  align='center'
                  onClick={() => onRowClick(study_id)}
                  style={getColumnCellStyle('risk')}>
                  {risk_module && (
                    <ExaminationTableRisk
                      exam_id={study_id}
                      currentExam={currentExam}
                      form_filled={form_filled}
                    />
                  )}
                </TableCell>
              )}
              {/* Actions */}
              <ExaminationTableActions
                study_id={study_id}
                patient_id={patient_id}
                isNestedExam={false}
                nestedRowTitle={isRowExpanded && row.length > 1}
                singleExam={row.length >= 1}
                prior={false}
                isRemoved={!!is_removed}
                priorList={row.length > 1 ? row.map((item) => item.id) : []}
                handleRowClick={() => onRowClick(study_id)}
                columnWidth={props.columnWidths?.actions}
              />
            </TableRow>

            {/* Nested examinations row container */}
            {row.length > 1 && (
              <TableRow>
                <TableCell
                  style={{
                    padding: '0',
                    backgroundColor: theme.palette.background.highlightDarker,
                    borderLeft: `6px solid ${theme.palette.background.highlightLight}`,
                  }}
                  colSpan={(props.expandedPatientList ? 9 : 7) + (bulkAction ? 1 : 0)}>
                  <Collapse in={isRowExpanded} timeout='auto' unmountOnExit>
                    <Table size='small' aria-label='nestedTable'>
                      <TableBody>{row.map((item, i) => nestedTable(item, i))}</TableBody>
                    </Table>
                  </Collapse>
                </TableCell>
              </TableRow>
            )}
          </React.Fragment>
        );
      })}

      <TableRow>
        {fetchingExamList ? (
          <TableCell
            colSpan={(props.expandedPatientList ? 9 : 7) + (bulkAction ? 1 : 0)}
            align='center'
            style={{ borderBottom: 'none', opacity: '0.6' }}>
            <CircularProgress color='primary' size={14} />
          </TableCell>
        ) : (
          <TableCell
            colSpan={(props.expandedPatientList ? 9 : 7) + (bulkAction ? 1 : 0)}
            ref={endListRef}
            align='center'
            style={{
              borderBottom: 'none',
              borderTop: `2px solid ${theme.palette.accents.light}`,
              height: '20px',
            }}>
            {exam_list.length < totalExaminations && !fetchingExamList && (
              <Tooltip
                title={<Typography className={classes.tooltip_texts}>{t('Table.fetch_more')}</Typography>}
                placement='bottom'
                arrow>
                <span>
                  <IconButton
                    size='small'
                    onClick={() => fetchMorePatients()}
                    className={isHidden ? classes.hidden : ''}>
                    <ControlPointIcon color='disabled' fontSize='small' />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </TableCell>
        )}
      </TableRow>
    </TableBody>
  );
};

export default ExaminationTableRows;
